import React from 'react';
import { motion } from 'framer-motion';
import VisualizationRenderer from '../AISearchVisualization/AISearchVisualization';

/**
 * MessageRenderer Component
 * Renders AI message text with formatting and visualizations
 * Used in both AISearch and SavedSummaries for consistent display
 */
const MessageRenderer = ({ 
  text, 
  visualizations = null, 
  isDark = false,
  showAnimations = false 
}) => {
  
  // Debug: Log visualization data
  React.useEffect(() => {
    if (visualizations) {
      console.log('📊 [MESSAGE RENDERER] Rendering with visualizations:', {
        isArray: Array.isArray(visualizations),
        count: Array.isArray(visualizations) ? visualizations.length : 1,
        data: visualizations
      });
    }
  }, [visualizations]);
  
  // Format text with basic markdown-like transformations
  const formatText = (rawText) => {
    // Ensure rawText is a string - convert if needed
    if (rawText === null || rawText === undefined) return '';
    
    // Convert to string if it's not already and capture as const for stable reference
    const originalString = String(rawText);
    let cleaned = originalString;
    
    // First, remove visualization tags more carefully to preserve word boundaries
    cleaned = cleaned.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
      // Ensure string is actually a string before using substring
      // Always convert to string to handle any edge cases
      const str = String(string || originalString);
      // Check if we're breaking a word by checking characters before and after
      const before = str.substring(Math.max(0, offset - 1), offset);
      const after = str.substring(offset + match.length, offset + match.length + 1);
      
      // If breaking a word (alphanumeric before and after), preserve space
      const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
      
      // If there's whitespace before or after, keep it; otherwise add space if breaking word
      const hasSpaceBefore = /\s/.test(before);
      const hasSpaceAfter = /\s/.test(after);
      
      if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
        return ' '; // Add space to prevent word breaking
      }
      
      return ''; // Remove visualization tag
    });
    
    // Remove standalone JSON visualization objects (more carefully)
    cleaned = cleaned.replace(/\{\s*"type"\s*:\s*"(line|bar|pie|table)"[\s\S]{0,15000}?\}/gi, (match, offset, string) => {
      // Ensure string is actually a string before using substring
      // Always convert to string to handle any edge cases
      const str = String(string || cleaned);
      // Similar word boundary check
      const before = str.substring(Math.max(0, offset - 1), offset);
      const after = str.substring(offset + match.length, offset + match.length + 1);
      
      const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
      const hasSpaceBefore = /\s/.test(before);
      const hasSpaceAfter = /\s/.test(after);
      
      if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
        return ' ';
      }
      
      return '';
    });
    
    // Clean up multiple consecutive newlines
    cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
    
    // Clean up extra spaces that might have been created
    cleaned = cleaned.replace(/\s{3,}/g, ' '); // Replace 3+ spaces with single space
    
    // Format markdown
    cleaned = cleaned
      .replace(/^###?\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');
    
    return cleaned;
  };

  return (
    <div className="message-renderer">
      {/* Message Text */}
      <div 
        className="text-base leading-relaxed whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100"
        dangerouslySetInnerHTML={{
          __html: formatText(text)
        }}
      />
      
      {/* Visualizations (Charts, Tables, etc.) */}
      {visualizations && Array.isArray(visualizations) && visualizations.length > 0 && (
        <div className="mt-4 space-y-4">
          {visualizations.map((viz, index) => (
            showAnimations ? (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="w-full"
              >
                <VisualizationRenderer 
                  data={viz} 
                  isDark={isDark}
                />
              </motion.div>
            ) : (
              <div key={index} className="w-full">
                <VisualizationRenderer 
                  data={viz}
                  isDark={isDark}
                />
              </div>
            )
          ))}
        </div>
      )}
      
      {/* Single Visualization (non-array) */}
      {visualizations && !Array.isArray(visualizations) && (
        <div className="mt-4">
          <VisualizationRenderer 
            data={visualizations}
            isDark={isDark}
          />
        </div>
      )}
    </div>
  );
};

export default MessageRenderer;

